import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';

import { USER_ROLE_ADMIN, USER_ROLE_PARTNER } from 'common/userRoles';

import { SR_API_ACTION_RENEWALS_CREATE, SR_API_ACTION_DISCOUNTS_CREATE } from 'api/actionTypes';

import postWithState from 'client/actions/postWithState';
import PartnerActions from 'client/actions/Partner';
import MarketActions from 'client/actions/Market';
import ListingActions from 'client/actions/Listing';

import { SCREEN_ID_PARTNER_BILLING_PAGE } from 'client/constants/screenIds';

import branch from 'client/core/branch';
import NavigatorActions from 'client/actions/Navigator';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withScreenId from 'client/helpers/withScreenId';
import allowedRoles from 'client/helpers/allowedRoles';

import SrPartnerPageV2, {
  SR_PARTNER_PAGE_NAVBAR_THEME_ROUNDED,
  SR_PARTNER_PAGE_THEME_BILLING,
} from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrPartnerBillingSection from 'client/components/SrPartnerBillingSection/SrPartnerBillingSection';

@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_ADMIN, USER_ROLE_PARTNER],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@branch(({ match }) => ({
  currentUser: ['currentUser'],
  currentPartner: ['partners', 'currentPartner'],
  partnersBySlug: ['partners', 'bySlug'],
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  listingsEligibleForDiscounts: [
    'listingsEligibleForDiscounts',
    'byPartnerSlug',
    match.params.partnerSlug,
  ],
  markets: ['markets', 'byId'],
  appUrl: ['env', 'APP_URL'],
}))
@withScreenId(SCREEN_ID_PARTNER_BILLING_PAGE)
@withScrollToTop
class AdminPartnerBillingPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Partner Billing',
    section: 'Partner',
  });

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    match: PropTypes.object.isRequired,
    currentUser: PropTypes.object,
    currentPartner: PropTypes.object,
    partnersBySlug: PropTypes.object,
    partner: PropTypes.object,
    markets: PropTypes.object,
    appUrl: PropTypes.string,
  };

  static fetchData(match, inject) {
    const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);
    const promisesToResolve = lodashCompact([
      !partner &&
        PartnerActions.loadPartnerBySlug(inject, {
          slug: match.params.partnerSlug,
        }),
      MarketActions.getList(inject),
      ListingActions.getListingsEligibleForDiscounts(inject, {
        partnerSlug: match.params.partnerSlug,
        forceRefresh: true,
      }),
    ]);

    return Promise.all(promisesToResolve);
  }

  _onBack = () => {
    const { dispatch, match } = this.props;
    dispatch(NavigatorActions.goBack, {
      defaultTo: `/partner/${match.params.partnerSlug}`,
    });
  };

  render() {
    const {
      partner,
      markets,
      currentUser,
      listingsEligibleForDiscounts,
      partnersBySlug,
      match,
      dispatch,
    } = this.props;

    const currentPartner = match.params.partnerSlug
      ? partnersBySlug[match.params.partnerSlug]
      : this.props.currentPartner;

    const navbarProps = {
      ...(partner?.logoUrl
        ? { navbarImage: partner.logoUrl }
        : { navbarTitle: partner.displayName }),
      navBarTheme: SR_PARTNER_PAGE_NAVBAR_THEME_ROUNDED,
      navbarMaxWidth: 'none',
      onBack: this._onBack,
    };

    const marketsArray = Object.values(markets) || [];

    return (
      <SrPartnerPageV2
        theme={SR_PARTNER_PAGE_THEME_BILLING}
        maxWidth="1180px"
        currentUser={currentUser}
        currentPartner={currentPartner}
        {...navbarProps}
      >
        <SrPartnerBillingSection
          currentUser={currentUser}
          partner={partner}
          handleRenewalSubmit={(payload) => {
            return dispatch(postWithState, {
              action: SR_API_ACTION_RENEWALS_CREATE,
              payload: {
                ...payload,
                marketId: parseInt(payload.marketId, 10),
                partnerId: partner.id,
              },
            });
          }}
          handleDiscountSubmit={(payload) => {
            return dispatch(postWithState, {
              action: SR_API_ACTION_DISCOUNTS_CREATE,
              payload: {
                ...payload,
                partnerId: partner.id,
              },
            });
          }}
          markets={marketsArray}
          listingsEligibleForDiscounts={listingsEligibleForDiscounts}
        />
      </SrPartnerPageV2>
    );
  }
}

AdminPartnerBillingPage.displayName = 'AdminPartnerBillingPage';

export default AdminPartnerBillingPage;
